import { Box, Button, Divider, Modal, Stack, Typography } from "@mui/material";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";

import { CloseIcon } from "@/assets";
import { CheckListIcon } from "@/assets/icons/CheckListIcon";

const styles = {
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  modalContainer: {
    width: "600px",
    bgcolor: "background.paper",
    boxShadow: 24,
    borderRadius: 2,
    outline: "none",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "30px",
  },
  headerText: {
    fontWeight: 700,
    fontSize: "20px",
    lineHeight: "30px",
    color: "#333333",
  },
  closeIcon: {
    cursor: "pointer",
    fontSize: "18px",
  },
  content: {
    display: "flex",
    flexDirection: "column",
    padding: "30px",
    gap: "30px",
  },
  contentText: {
    fontWeight: 700,
    fontSize: "18px",
    lineHeight: "28px",
    color: "#333333",
  },
  contentSubText: {
    fontWeight: 400,
    fontSize: "18px",
    lineHeight: "28px",
    color: "#333333",
  },
  actionsContainer: {
    padding: "30px",
  },
  actionsRow: {
    height: "45px",
  },
  icon: {
    fontSize: "26px",
  },
};

interface ReportCreatedModalProps {
  open: boolean;
  setOpen: (isOpen: boolean) => void;
  onOpenReport: () => void;
  onDownloadReport: () => void;
  canOpenReport?: boolean;
}

export const ReportCreatedModal = ({
  open,
  setOpen,
  onOpenReport,
  onDownloadReport,
  canOpenReport = true,
}: ReportCreatedModalProps): JSX.Element => {
  const { t } = useTranslation("ValuationReportGenerationPanel");

  const handleDownloadReport = (): void => {
    onDownloadReport();
    setOpen(false);
  };

  const handleOpenReport = (): void => {
    onOpenReport();
    setOpen(false);
  };

  return (
    <Modal open={open} sx={styles.modal}>
      <Box sx={styles.modalContainer} data-cy="report-created-modal">
        <Box sx={styles.header}>
          <Stack direction="row" gap="15px" alignItems={"center"}>
            <CheckListIcon sx={styles.icon} />
            <Typography sx={styles.headerText}>{t("reportPanel.reportCreatedModal.header")}</Typography>
          </Stack>
          <CloseIcon
            sx={styles.closeIcon}
            onClick={() => {
              setOpen(false);
            }}
            color="primary"
            data-cy="report-created-modal-close"
          />
        </Box>
        <Divider />
        <Box sx={styles.content}>
          <Typography sx={styles.contentText}>{t("reportPanel.reportCreatedModal.text")}</Typography>
          <Typography sx={styles.contentSubText}>{t("reportPanel.reportCreatedModal.subText")}</Typography>
        </Box>
        <Box sx={styles.actionsContainer}>
          <Stack spacing={"30px"} direction="row" justifyContent="end" sx={styles.actionsRow}>
            <Button onClick={handleDownloadReport} color="secondary" variant="contained">
              {t("reportPanel.reportCreatedModal.downloadAgainButton")}
            </Button>
            {canOpenReport ? (
              <Button autoFocus onClick={handleOpenReport} variant="contained" color="primary">
                {t("reportPanel.reportCreatedModal.openButton")}
              </Button>
            ) : null}
          </Stack>
        </Box>
      </Box>
    </Modal>
  );
};
